import React, { useMemo } from 'react'
import { isNil } from 'lodash'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { EditStaticTariffForm } from './EditStaticTariffForm'
import { EditDynamicTariffForm } from './EditDynamicTariffForm'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EditTariffFormData, createEditTariffSchema } from '../../models/tariffs-form-model'
import { useEditTariff } from '../../api/use-edit-tariff'
import { editTariffsFormToDto } from '../../transformers/edit-tariff-form-to-dto-model'
import { TariffCategory } from 'src/Libs/e-mobility/shared/models/types'
import { TariffDetailsModel } from '../../models/single-tariff-model'

export interface EditTariffDialogProps {
  open: boolean
  onClose: () => void
  place: number
  tariff: TariffDetailsModel
  installationId: number
  tariffId: string
}

/**
 * Dialog component for edit tariff which is open on click edit tariff inside 3 dot menu.
 * It holds edit tariff form with Formik wrapper, initial values, validation schema and submit handler
 */
export const EditTariffDialog: React.FC<EditTariffDialogProps> = ({ open, onClose, place, tariff, installationId, tariffId }) => {
  const { t } = useCustomTranslation()
  const isDynamicTariff = tariff.tariffCategory === TariffCategory.dynamicTariff

  const validationSchema = useMemo(() => createEditTariffSchema(t), [t])

  const defaultValues: EditTariffFormData = useMemo(
    () => ({
      tariffId,
      tariffName: tariff.tariffName,
      tariffGridPrice: tariff.staticGridPrice.toString(),
      tariffSolarPrice: isNil(tariff.solarPrice) ? null : tariff.solarPrice.toString(),
      tariffDischargePrice: isNil(tariff.dischargePrice) ? null : tariff.dischargePrice.toString(),
      startDate: tariff.startTime || null,
      endDate: tariff.endTime || null,
      currencyCode: tariff.currencyCode,
    }),
    [tariff, tariffId]
  )

  const form = useForm<EditTariffFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const {
    getValues,
    formState: { isDirty, isValid },
  } = form

  const { isLoading, editTariff } = useEditTariff(onClose, installationId)

  const handleSubmit = (): void => {
    const payload = editTariffsFormToDto(getValues(), installationId)
    editTariff(payload)
  }

  return (
    <FormProvider {...form}>
      <Dialog
        maxWidth="sm"
        title={isDynamicTariff ? t('emobility.tariffs.edit-dynamic-tariff-title') : t('emobility.tariffs.edit-static-tariff-title')}
        onClose={onClose}
        place={place}
        actions={{
          primary: {
            disabled: !isDirty || !isValid || isLoading,
            isLoading,
            name: t('emobility.tariffs.save'),
            onClick: handleSubmit,
          },
          secondary: { name: t('emobility.tariffs.cancel') },
        }}
      >
        {isDynamicTariff ? <EditDynamicTariffForm /> : <EditStaticTariffForm />}
      </Dialog>
    </FormProvider>
  )
}
